import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/portail')) {
        window.location.href = '/connexion';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data, inviteToken) => api.post(`/auth/register${inviteToken ? `?invite_token=${inviteToken}` : ''}`, data),
  getMe: () => api.get('/auth/me'),
  validateInvite: (token) => api.get(`/auth/invite/${token}`),
};

// Invitations APIs
export const invitationsAPI = {
  send: (data) => api.post('/invitations', data),
  list: () => api.get('/invitations'),
  cancel: (id) => api.delete(`/invitations/${id}`),
  resend: (id) => api.post(`/invitations/${id}/resend`),
};

// Client APIs
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  // Phase 2: Extended client APIs
  getProfile: (id) => api.get(`/clients/${id}/profile`),
  getProjects: (id) => api.get(`/clients/${id}/projects`),
  getAllFiles: (id, projectId, statut) => {
    let url = `/clients/${id}/files`;
    const params = [];
    if (projectId) params.push(`project_id=${projectId}`);
    if (statut) params.push(`statut=${statut}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  getRequiredDocs: (id) => api.get(`/clients/${id}/required-documents`),
  updateStatus: (id, statut) => api.put(`/clients/${id}/status?statut=${statut}`),
};

// Project APIs
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getMembers: (id) => api.get(`/projects/${id}/members`),
  addMember: (id, data) => api.post(`/projects/${id}/members`, data),
};

// Folder APIs
export const foldersAPI = {
  getByProject: (projectId) => api.get(`/folders/project/${projectId}`),
  create: (data) => api.post('/folders', data),
  delete: (id) => api.delete(`/folders/${id}`),
};

// File APIs
export const filesAPI = {
  getByProject: (projectId, folderId) => {
    let url = `/files/project/${projectId}`;
    if (folderId) url += `?dossier_id=${folderId}`;
    return api.get(url);
  },
  getById: (id) => api.get(`/files/${id}`),
  upload: (file, projectId, folderId) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = `/files/upload?project_id=${projectId}`;
    if (folderId) url += `&dossier_id=${folderId}`;
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateStatus: (id, statut) => api.put(`/files/${id}/status`, { statut }),
  getHistory: (id) => api.get(`/files/${id}/history`),
  delete: (id) => api.delete(`/files/${id}`),
  search: (query, statut) => {
    let url = `/files/search/query?q=${encodeURIComponent(query)}`;
    if (statut) url += `&statut=${statut}`;
    return api.get(url);
  },
  getDownloadUrl: (id) => `${API_URL}/files/${id}/download`,
};

// Required Documents APIs
export const requiredDocsAPI = {
  getByProject: (projectId) => api.get(`/required-documents/project/${projectId}`),
  create: (data) => api.post('/required-documents', data),
  upload: (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/required-documents/${docId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/required-documents/${id}`),
};

// Notification APIs
export const notificationsAPI = {
  getAll: (limit = 50) => api.get(`/notifications?limit=${limit}`),
  getUnreadCount: () => api.get('/notifications/unread/count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
};

// Contact APIs
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: () => api.get('/contact'),
  markProcessed: (id) => api.put(`/contact/${id}/traite`),
};

// Admin APIs
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAuditLog: (limit = 100) => api.get(`/admin/audit-log?limit=${limit}`),
  getUsers: () => api.get('/admin/users'),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  assignUserToClient: (userId, clientId) => api.put(`/admin/users/${userId}/client?client_id=${clientId}`),
  toggleUserActive: (userId) => api.put(`/admin/users/${userId}/toggle-active`),
  createEmployee: (data) => api.post('/admin/users/employee', data),
};

// Leave Requests APIs
export const leaveAPI = {
  getAll: () => api.get('/leave-requests'),
  create: (data) => api.post('/leave-requests', data),
  review: (leaveId, status) => api.put(`/leave-requests/${leaveId}/review?status=${status}`),
};

// Availability APIs
export const availabilityAPI = {
  getMine: () => api.get('/availability'),
  update: (data) => api.put('/availability', data),
  getAll: () => api.get('/availability/all'),
};

// ==================== PHASE 2 & 3 APIs ====================

// Messages APIs
export const messagesAPI = {
  getByClient: (clientId, projectId) => {
    let url = `/messages/client/${clientId}`;
    if (projectId) url += `?project_id=${projectId}`;
    return api.get(url);
  },
  send: (data) => api.post('/messages', data),
  addAttachment: (messageId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/messages/${messageId}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  markAsRead: (id) => api.put(`/messages/${id}/read`),
};

// Comments APIs
export const commentsAPI = {
  getByFile: (fileId) => api.get(`/comments/file/${fileId}`),
  add: (fileId, contenu) => api.post(`/comments/file/${fileId}`, { contenu }),
};

// Approvals APIs
export const approvalsAPI = {
  getByFile: (fileId) => api.get(`/approvals/file/${fileId}`),
  submit: (fileId, status, commentaire) => api.post(`/approvals/file/${fileId}`, { status, commentaire }),
};

// Archive APIs
export const archiveAPI = {
  archiveProject: (id) => api.post(`/archive/project/${id}`),
  restoreProject: (id) => api.post(`/archive/project/${id}/restore`),
  archiveFile: (id) => api.post(`/archive/file/${id}`),
  restoreFile: (id) => api.post(`/archive/file/${id}/restore`),
  getArchivedProjects: () => api.get('/archive/projects'),
  getArchivedFiles: () => api.get('/archive/files'),
};

// Export APIs
export const exportAPI = {
  getProjectZipUrl: (projectId, approvedOnly = false) => 
    `${API_URL}/export/project/${projectId}?approved_only=${approvedOnly}`,
};

// Global Search API
export const searchAPI = {
  globalSearch: (query) => api.get(`/search?q=${encodeURIComponent(query)}`),
};

export default api;
